import PageHeader from '../components/PageHeader.jsx'
import { IconArrow } from '../components/icons.jsx'
import styles from './Presentations.module.css'

/**
 * Presentations — a LAUNCHER into the standalone customer video/asset portal
 * at present.srchamp.com, the same way the Pricing tab deep-links the pricing
 * engine.
 *
 * Deliberately read-only and deliberately NOT embedded. The portal gates each
 * film behind its own password and records the view; iframing it behind our JWT
 * would either break that gate or duplicate it, and re-implementing its backend
 * here would give the hub a second, drifting copy of the viewing record. The
 * hub's job is to be the front door — the portal stays the source of truth for
 * who watched what.
 */
const PORTAL_URL = 'https://present.srchamp.com'
const ADMIN_URL = 'https://present.srchamp.com/admin'

export default function Presentations() {
  return (
    <div className="fade-up">
      <PageHeader
        eyebrow="Customer-facing"
        title="Presentations"
        lead="Password-gated, trackable presentation films & assets shared with customers — sent as a private link, watched on their schedule, and tracked so you know exactly when it landed."
      />

      <div className={styles.hero}>
        <div className={styles.heroMain}>
          <img
            className={styles.seal}
            src="/brand/sr-monogram.png"
            alt=""
            width="46"
            height="46"
            aria-hidden="true"
          />

          <div className={styles.badge}>
            <span className={styles.badgeDot} />
            Live · standalone portal
          </div>

          <h2 className={styles.heroTitle}>Open the portal to share a film.</h2>
          <p className={styles.heroLead}>
            The portal runs as its own secure application at{' '}
            <span className={styles.mono}>present.srchamp.com</span>. Each
            presentation sits behind its own password, so a customer sees only
            what was sent to them. It opens in a new tab so you keep the hub
            open alongside it.
          </p>

          <div className={styles.actions}>
            <a
              className={`btn btn-primary ${styles.cta}`}
              href={PORTAL_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open the presentation portal
              <IconArrow width={17} height={17} />
            </a>
            <a
              className={`btn ${styles.ctaGhost}`}
              href={ADMIN_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Tracking dashboard
            </a>
          </div>
          <span className={styles.ctaNote}>
            Both open in a new tab · the portal has its own sign-in
          </span>
        </div>

        <ul className={styles.points}>
          <li>
            <strong>Password-gated per presentation</strong> — a customer link
            unlocks that film and nothing else.
          </li>
          <li>
            <strong>Tracked</strong> — the{' '}
            <span className={styles.mono}>/admin</span> dashboard records who
            opened a presentation and when, so follow-up is timed off real
            signal instead of a guess.
          </li>
          <li>
            <strong>Films &amp; assets</strong> — screening films, proposal
            decks, and the supporting files that go with them, in one place.
          </li>
          <li>
            <strong>Hurricanes screening</strong> — the first entry live in the
            portal today.
          </li>
        </ul>
      </div>

      <p className={styles.legacy}>
        Presentations intentionally runs as its own application; the hub links
        to it rather than embedding it, so the per-film password gate and the
        viewing record stay in one place. If a button doesn’t open, go directly
        to{' '}
        <a
          className={styles.legacyLink}
          href={PORTAL_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          present.srchamp.com
        </a>{' '}
        or{' '}
        <a
          className={styles.legacyLink}
          href={ADMIN_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          present.srchamp.com/admin
        </a>
        .
      </p>
    </div>
  )
}
